// DNS-Health-Store fuer die Sicherheit-Karte und den Dashboard-Banner.
// Der ENTPRELLTE Resolver-Zustand aus dns_state (Backend probed zentral pro
// Tick), nicht eine Live-Probe pro Request. Siehe docs/dns-health-state.md.
//
// Datenfluss (seit 03.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Erreichbarkeit, DNS und Bypass über das Protokoll“): der Zustand ist das
// Topic dns.lage. Das Backend (internal/protokoll/erreichbarkeit) setzt es auf
// Anstoß (jede Probe des Checkers) und alle 30 s; der Hub verwirft
// Unverändertes (Totband 20 ms für die Antwortzeit). Bis dahin holte jeder Tab
// /v2-api/dns/status alle 30 s.
//   - Hier steht die einzige Abo-Stelle. REST (`GET /v2-api/dns/status`) nur beim
//     Start, als Revalidator (Reconnect, Tab kommt zurück) und als Neuversuch
//     alle 15 s, solange ein Abruf scheiterte. KEIN 30-s-Takt mehr.
//   - Ein Stand, der während eines Abrufs kommt, liegt über dessen Antwort (sie
//     kann dahinter liegen); ein älterer Stand überschreibt eine frischere
//     Antwort nicht.
//
// SWR: nein -- ein gemerkter "down" würde nach dem Reload kurz aufblitzen.

import { getJSON } from "./api";
import { dnsAusProtokoll } from "./erreichbarkeit-protokoll";
import { abonniere } from "./protokoll/client.svelte";
import type { DnsLage } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import { gleich } from "./storage-protokoll";

export interface DnsHealth {
  ok: boolean;
  // Entprellter Zustand aus dem Backend. Der Banner haengt an
  // health === "down", NICHT am rohen ok -- ein einzelner Probe-Blip
  // kippt damit nichts mehr.
  health?: "up" | "down" | "unknown" | string;
  server: string;
  mode: "adguard" | "fallback" | string;
  /** seit wann der entprellte Zustand gilt (RFC 3339) */
  since?: string;
  flap_count?: number;
  latency_ms?: number;
}

let _state = $state.raw<DnsHealth | undefined>(undefined);
// Laufende Nummer des jüngsten Stands: ein Abruf, der vor einem Stand startete
// und danach ankommt, ist nicht neuer als dieser.
let _nr = 0;
let _started = false;
let _unsub: Array<() => void> = [];

function setze(neu: DnsHealth): void {
  if (_state && gleich(_state, neu)) return;
  _state = neu;
}

function beiLage(_instanz: string, daten: Record<string, unknown>): void {
  _nr++;
  setze(dnsAusProtokoll(skaliere<DnsLage>("dns.lage", daten)));
}

// Nach einem gescheiterten Abruf ein neuer Versuch (es gibt keinen Takt mehr).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;
// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _nr;
  const lauf = _lauf;
  let rest: DnsHealth | undefined;
  try {
    // v2-Endpoint liefert die DNS-Struktur direkt (im Gegensatz zum
    // alten /api/health/status, das DNS in einem groesseren Aggregat
    // verschachtelt hatte). Eine Indirektion weniger.
    const j = await getJSON<DnsHealth>("/v2-api/dns/status");
    rest = j && typeof j.ok === "boolean" ? j : undefined;
  } catch {
    rest = undefined;
  }
  if (lauf !== _lauf) return;
  if (rest) {
    if (_nr <= ab) setze(rest);
  } else if (_started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void laden();
    }, NEUVERSUCH_MS);
  }
}

export function startDnsHealth(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  void laden();
  _unsub.push(abonniere("dns.lage", { rate: "normal" }, { stand: beiLage }));
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT: nach einem abgerissenen Kanal
  // (jeder Deploy) holt der Revalidator den Stand frisch.
  _unsub.push(registerRevalidator(laden));
}

export function stopDnsHealth(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _state = undefined;
  _nr = 0;
}

export function getDnsHealth(): DnsHealth | undefined {
  return _state;
}
